interface EventMetadata {
  [key: string]: string | number | boolean | null | undefined
}

interface AnalyticsEvent {
  name: string
  meta?: EventMetadata
}

interface ProductAnalyticsConfig {
  client_token: string
  endpoint: string
}

export async function publishEvents(
  events: AnalyticsEvent[],
  config: ProductAnalyticsConfig,
) {
  const { client_token, endpoint } = config

  if (events.length > 10) {
    throw new Error("Cannot publish more than 10 events at once")
  }

  if (!client_token) {
    throw new Error("Product Analytics token not available")
  }

  const payload = JSON.stringify({ events })
  const contentSha256Value = await contentSha256(payload)

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("POST", endpoint)
    xhr.setRequestHeader("Content-Type", "application/json; charset=utf-8")
    xhr.setRequestHeader("X-Authorization", `Bearer ${client_token}`)
    xhr.setRequestHeader("X-Amz-Content-Sha256", contentSha256Value)

    xhr.onload = () => resolve()
    xhr.onerror = (e) => reject(e)
    xhr.send(payload)
  })
}

export async function publishEvent(
  name: string,
  meta: EventMetadata = {},
  config: ProductAnalyticsConfig,
) {
  return publishEvents([{ name, meta }], config)
}

async function contentSha256(payload: string): Promise<string> {
  if (!window.crypto?.subtle) {
    throw new Error(
      "Web Crypto API not available. Product Analytics requires HTTPS or localhost in a secure context.",
    )
  }

  const textBuffer = new TextEncoder().encode(payload)
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", textBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("")
}
